import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WhatYouGetSection, HowItWorksSection, PricingSection, FAQSection, FooterCTASection } from '../components/home/HomeSections';
import { TripBuilderForm } from '../components/TripBuilder';
import { BlogPreviewSection } from '../components/home/BlogPreviewSection';
import { HomePricingModal } from '../components/home/HomePricingModal';
import '../styles/App.css';
import './HomePage.css';

// SVG Icons
const StarIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const UserIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 9l6 6 6-6" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
);

export default function HomePage() {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'ro' ? 'ro' : 'en';
    const heroFormRef = useRef<HTMLDivElement>(null);
    const [showPricingModal, setShowPricingModal] = useState(false);

    // Scroll to form and focus first input
    const scrollToForm = useCallback(() => {
        if (heroFormRef.current) {
            heroFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus on first input after scroll animation
            setTimeout(() => {
                const firstInput = heroFormRef.current?.querySelector('input, button[aria-haspopup]');
                if (firstInput instanceof HTMLElement) {
                    firstInput.focus();
                }
            }, 500);
        }
    }, []);

    // Scroll to explore section
    const scrollToExplore = useCallback(() => {
        const exploreSection = document.getElementById('explore-section');
        if (exploreSection) {
            exploreSection.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    // Open pricing modal
    const openPricingModal = useCallback(() => {
        setShowPricingModal(true);
    }, []);

    return (
        <div className="home-page home-page-v2">
            {/* ===== HERO SECTION ===== */}
            <section className="hero hero-v2">
                <div className="hero-overlay" />
                <div className="hero-content">
                    {/* Badge */}
                    <div className="hero-badge">
                        <StarIcon />
                        <span>{t('home.hero.badge')}</span>
                    </div>

                    {/* Main heading */}
                    <h1 className="hero-title">
                        {t('home.hero.title')}
                    </h1>

                    {/* Subtitle */}
                    <p className="hero-subtitle">
                        {t('home.hero.subtitle')}
                    </p>

                    {/* Form label */}
                    <div className="hero-form-label">
                        {t('home.hero.formLabel')}
                    </div>

                    {/* Trip Builder Form */}
                    <div ref={heroFormRef} className="hero-form-wrapper">
                        <TripBuilderForm 
                            buttonText={t('home.hero.formButton')}
                        />
                    </div>

                    {/* Form subtext */}
                    <p className="hero-form-subtext">
                        {t('home.hero.formSubtext')}
                    </p>

                    {/* Explore CTA */}
                    <button 
                        className="hero-explore-cta"
                        onClick={scrollToExplore}
                    >
                        {t('home.hero.exploreCta')}
                        <ArrowRightIcon />
                    </button>

                    {/* Social proof */}
                    <div className="hero-social-proof">
                        <div className="avatars">
                            <div className="avatar-stack">
                                <span><UserIcon /></span>
                                <span><UserIcon /></span>
                                <span><UserIcon /></span>
                            </div>
                        </div>
                        <p>{t('home.hero.socialProof')}</p>
                    </div>

                    {/* Scroll indicator */}
                    <button 
                        className="scroll-hint" 
                        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                    >
                        <span>{t('home.hero.scrollHint')}</span>
                        <ChevronDownIcon />
                    </button>
                </div>
            </section>

            {/* ===== WHAT YOU GET ===== */}
            <WhatYouGetSection />

            {/* ===== EXPLORE (Blog Preview) ===== */}
            <BlogPreviewSection locale={locale} id="explore-section" />
            
            {/* ===== HOW IT WORKS ===== */}
            <HowItWorksSection />
            
            {/* ===== PRICING ===== */}
            <PricingSection onOpenModal={openPricingModal} />
            
            {/* ===== FAQ ===== */}
            <FAQSection />
            
            {/* ===== FOOTER CTA ===== */}
            <FooterCTASection onScrollToForm={scrollToForm} />

            {/* ===== PRICING MODAL ===== */}
            <HomePricingModal 
                isOpen={showPricingModal} 
                onClose={() => setShowPricingModal(false)} 
            />
        </div>
    );
}
